<template>
	<div class="calculator-popup-wrapper">
		<vs-popup
			:active.sync="active"
			title="Calculatrice de Prix de Vente"
			:fullscreen="false"
			@close="closeCalculator"
			class="calculator-popup"
			style="z-index: 100000 !important"
		>
			<div class="calculator-container">
				<!-- Number Input Field -->
				<div class="input-group">
					<label class="input-label">Entrer un prix d'achats:</label>
					<vs-input
						ref="numberInput"
						v-model="numberValue"
						type="number"
						placeholder="0"
						class="number-input"
						@keyup.enter="calculate"
					/>
				</div>

				<!-- Results Display -->
				<div v-if="showResults" class="results-section">
					<div class="result-item">
						<span class="result-label">Tx Marge:</span>
						<span class="result-value">{{ marginPercentage }}</span>
					</div>
					<div class="result-item">
						<span class="result-label">Prix Vente:</span>
						<span class="result-value">{{ formatCurrency(salePrice) }}</span>
					</div>
				</div>

				<!-- Calculate Button -->
				<div class="button-group">
					<vs-button
						color="primary"
						type="filled"
						size="large"
						class="calculate-button"
						@click="calculate"
					>
						Calculer
					</vs-button>
				</div>
			</div>
		</vs-popup>
	</div>
</template>

<script>
export default {
	name: "CalculatorPopup",
	props: {
		popupActive: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			active: false,
			numberValue: "",
			showResults: false,
			salePrice: 0,
			marginPercentage: "0%",
		};
	},
	watch: {
		popupActive(val) {
			this.active = val;
			if (val) {
				// Focus on input when popup opens
				this.$nextTick(() => {
					if (this.$refs.numberInput && this.$refs.numberInput.$el) {
						this.$refs.numberInput.$el.querySelector("input").focus();
					}
				});
			}
		},
		active(val) {
			this.$emit("update:popupActive", val);
			if (!val) {
				// Clear the input and hide results when closing
				this.numberValue = "";
				this.showResults = false;
			}
		},
	},
	mounted() {
		if (this.active) {
			this.$nextTick(() => {
				if (this.$refs.numberInput && this.$refs.numberInput.$el) {
					this.$refs.numberInput.$el.querySelector("input").focus();
				}
			});
		}
		// Move popup to body to ensure proper z-index
		this.$nextTick(() => {
			if (this.$el && document.body) {
				document.body.appendChild(this.$el);
			}
		});
	},
	beforeDestroy() {
		// Clean up when component is destroyed
		if (this.$el && this.$el.parentNode) {
			this.$el.parentNode.removeChild(this.$el);
		}
	},
	methods: {
		calculate() {
			// Check if number is entered
			if (!this.numberValue || this.numberValue === "") {
				this.$vs.notify({
					title: "Attention",
					text: "Veuillez entrer un nombre",
					color: "warning",
				});
				return;
			}

			const number = parseFloat(this.numberValue);

			if (isNaN(number)) {
				this.$vs.notify({
					title: "Erreur",
					text: "Veuillez entrer un nombre valide",
					color: "danger",
				});
				return;
			}

			// Calculate sale price and margin percentage
			this.salePrice = this.calculateSalePrice(number);
			this.marginPercentage = this.calculateMarginPercentage(
				number,
				this.salePrice
			);

			// Show the results
			this.showResults = true;

			// Emit the calculation results to parent component
			this.$emit("calculation-complete", {
				purchasePrice: number,
				salePrice: this.salePrice,
				marginPercentage: this.marginPercentage,
			});
		},

		calculateSalePrice(purchasePrice) {
			if (!purchasePrice) return 0;

			const pa =
				typeof purchasePrice === "string"
					? Number(purchasePrice)
					: purchasePrice;

			if (isNaN(pa)) return 0;

			let multiplier;

			if (pa < 250) {
				// 100% margin => gain = 1 × PA => PA + PA = 2PA
				multiplier = 2;
			} else if (pa >= 2000) {
				// 50% margin => gain = 0.5 × PA => 1.5PA
				multiplier = 1.5;
			} else {
				// Linear degressive margin from 2 (at 250€) to 1.5 (at 2000€)
				const slope = (2 - 1.5) / (250 - 2000); // negative slope
				multiplier = 2 + slope * (pa - 250);
			}

			return Math.round(pa * multiplier);
		},

		calculateMarginPercentage(pa, pv) {
			// If no purchase price, return N/A
			if (!pa || pa === 0) return "N/A";

			// If no sell price, margin is 0%
			if (!pv || pv === 0) return "0%";

			// If purchase price equals sale price, margin is 0%
			if (pa === pv) return "0%";

			return Math.round(((pv - pa) / pa) * 100) + "%";
		},

		formatCurrency(value) {
			if (!value) return "0 €";

			return new Intl.NumberFormat("fr-FR", {
				style: "currency",
				currency: "EUR",
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			}).format(value);
		},

		closeCalculator() {
			this.active = false;
		},
	},
};
</script>

<style>
/* Global styles for calculator popup (not scoped to ensure they apply) */
.calculator-popup-wrapper {
	position: fixed !important;
	top: 0 !important;
	left: 0 !important;
	width: 100% !important;
	height: 100% !important;
	pointer-events: none !important;
	z-index: 9999999 !important;
}

.calculator-popup-wrapper > * {
	pointer-events: auto !important;
}

.calculator-popup-wrapper .vs-popup {
	z-index: 9999999 !important;
}

.calculator-popup-wrapper .vs-popup--background {
	z-index: 9999998 !important;
}

.calculator-popup-wrapper .vs-popup__content {
	z-index: 9999999 !important;
}

.calculator-popup-wrapper .vs-popup--content {
	z-index: 9999999 !important;
}
</style>

<style scoped>
.calculator-container {
	padding: 30px;
	min-width: 350px;
}

.input-group {
	margin-bottom: 25px;
}

.input-label {
	display: block;
	font-size: 14px;
	font-weight: 600;
	color: #666;
	margin-bottom: 10px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.number-input {
	width: 100%;
}

.number-input >>> input {
	font-size: 18px;
	padding: 12px;
	text-align: center;
	font-family: "Monaco", "Menlo", monospace;
}

.button-group {
	display: flex;
	justify-content: center;
	margin-top: 20px;
}

.calculate-button {
	width: 100%;
	padding: 12px 40px;
	font-size: 16px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 1px;
}

.results-section {
	background-color: #f7f9fc;
	border: 1px solid #e0e6ed;
	border-radius: 8px;
	padding: 20px;
	margin: 20px 0;
	animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.result-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px 0;
}

.result-item:not(:last-child) {
	border-bottom: 1px solid #e0e6ed;
}

.result-label {
	font-size: 14px;
	font-weight: 600;
	color: #666;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.result-value {
	font-size: 18px;
	font-weight: bold;
	color: #08adb5;
	font-family: "Monaco", "Menlo", monospace;
}
</style>